import type { ChefeEstado, EventoAgenda, EventoAtual, PentaEstado } from '../../api/evento';
import { nomeDoMapa } from '../../i18n/nomes';
import { numero, t } from '../../i18n/idioma';
import { Selo } from './Selo';
import { formatar, formatarLongo, restanteDe } from './tempo';
import { EM_BREVE, TIPO_CHEFE, TIPO_PENTA, TIPO_UB, TIPOS_EVENTO, aoVivo, chefeVivo, proximoDo, situacaoDe, type TipoEvento } from './tipos';

interface Props {
  atual: EventoAtual | null;
  agenda: EventoAgenda | null;
  /** A barra do Chefe do Mundo (hub), para o "ao vivo" do cartão dele. */
  chefe?: ChefeEstado | null;
  /** A ponta desperta do Pentagrama (hub), para o "ao vivo" do cartão dele. */
  penta?: PentaEstado | null;
  busy: boolean;
  /** Por que não dá para se inscrever agora (morto, caçando, sem prata…), ou null. */
  razao: string | null;
  moeda: string;
  onAbrir(tipo: string): void;
  onInscrever(): void;
  onDesistir(): void;
}

/**
 * O quadro do dia em Mais › Eventos (techspec-eventos §08): a faixa de 24 h com os horários e o agora, o destaque do
 * evento que está rolando (com o botão de inscrever), um cartão por evento e os que vêm aí.
 */
export function Quadro({ atual, agenda, chefe = null, penta = null, busy, razao, moeda, onAbrir, onInscrever, onDesistir }: Props) {
  return (
    <>
      <FaixaDoDia agenda={agenda} vivo={aoVivo(atual) !== null || chefeVivo(chefe)} />
      <Destaque atual={atual} agenda={agenda} busy={busy} razao={razao} moeda={moeda} onInscrever={onInscrever} onDesistir={onDesistir} onAbrir={() => onAbrir(TIPO_UB)} />
      <DestaqueChefe chefe={chefe} onAbrir={() => onAbrir(TIPO_CHEFE)} />
      <section className="ev-sec">
        <h3 className="ev-sec__t">{t('evento.quadro.eventos')}</h3>
        <ul className="ev-cartoes">
          {TIPOS_EVENTO.map(x => (
            <Cartao key={x.id} tipo={x} atual={atual} agenda={agenda} chefe={chefe} penta={penta} onAbrir={() => onAbrir(x.id)} />
          ))}
        </ul>
      </section>
      {EM_BREVE.length > 0 && (
        <section className="ev-sec">
          <h3 className="ev-sec__t">{t('evento.quadro.emBreve')}</h3>
          <ul className="ev-cartoes">
            {EM_BREVE.map(x => (
              <li key={x.nome} className="ev-cartao ev-cartao--breve" aria-disabled="true">
                <Selo id={x.selo} apagado />
                <span className="ev-cartao__corpo">
                  <b className="ev-cartao__nome">{t(x.nome)}</b>
                  <span className="ev-cartao__frase">{t(x.frase)}</span>
                </span>
                <span className="ev-cartao__dir"><span className="ev-chip">{t('evento.chip.emBreve')}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** A faixa de hoje: o dia de Brasília inteiro, os horários marcados e onde o agora está. */
function FaixaDoDia({ agenda, vivo }: { agenda: EventoAgenda | null; vivo: boolean }) {
  if (!agenda?.diaInicioMs || !agenda.diaFimMs) return null;
  const ini = agenda.diaInicioMs;
  const dur = Math.max(1, agenda.diaFimMs - ini);
  const pos = (ms: number) => Math.min(100, Math.max(0, ((ms - ini) / dur) * 100));
  const agora = Date.now();
  const hoje = agenda.hoje ?? [];
  const umaRodada = ((agenda.inscricaoSegundos ?? 300) + (agenda.partidaSegundos ?? 900)) * 1000;
  const prox = hoje.find(h => h.emMs > agora) ?? null;
  const sub = hoje.length === 0
    ? t('evento.quadro.semHoje')
    : vivo
      ? t('evento.quadro.rolando')
      : prox
        ? t('evento.quadro.proximoEm', { hora: prox.hora, t: formatarLongo(restanteDe(prox.emMs)) })
        : t('evento.quadro.acabou');
  return (
    <section className="ev-sec">
      <h3 className="ev-sec__t">{t('evento.quadro.hoje')}<small>{sub}</small></h3>
      <div className="ev-dia" role="img" aria-label={hoje.map(h => h.hora).join(', ') || sub}>
        <span className="ev-dia__linha"><i style={{ width: `${pos(agora)}%` }} /></span>
        {hoje.map(h => {
          const estado = agora >= h.emMs + umaRodada ? 'feito' : agora >= h.emMs ? 'agora' : 'vem';
          return (
            <span key={h.id + h.emMs}>
              <span className={`ev-dia__ev is-${estado}${h.tipo === TIPO_CHEFE ? ' ev-dia__ev--chefe' : ''}`} style={{ left: `${pos(h.emMs)}%` }}
                title={`${t(h.tipo === TIPO_CHEFE ? 'chefe.nome' : 'evento.nome')} · ${h.hora}`} />
              <span className={`ev-dia__h is-${estado}`} style={{ left: `${pos(h.emMs)}%` }}>{h.hora}</span>
            </span>
          );
        })}
        <span className="ev-dia__agora" style={{ left: `${pos(agora)}%` }} />
      </div>
    </section>
  );
}

function Pips({ vivos, de }: { vivos: number; de: number }) {
  return (
    <span className="ev-pips" title={t('evento.selo.placar', { n: vivos })}>
      {Array.from({ length: Math.max(de, vivos) }, (_, i) => <i key={i} className={i < vivos ? '' : 'is-off'} />)}
    </span>
  );
}

/** O que está rolando agora, em destaque: o relógio e a ação, sem precisar abrir o evento. */
function Destaque({ atual, agenda, busy, razao, moeda, onInscrever, onDesistir, onAbrir }: {
  atual: EventoAtual | null; agenda: EventoAgenda | null; busy: boolean; razao: string | null; moeda: string;
  onInscrever(): void; onDesistir(): void; onAbrir(): void;
}) {
  const vivo = aoVivo(atual);
  if (!vivo || !atual) return null;
  const porSala = atual.porSala ?? agenda?.porSala ?? 6;
  const minimo = atual.minimo ?? agenda?.minimo ?? 2;
  return (
    <section className="ev-agora" aria-live="polite">
      <div className="ev-agora__t">
        <button type="button" className="ev-agora__abrir" onClick={onAbrir} aria-label={t('evento.nome')}>
          <Selo id="ub" />
        </button>
        <span className="ev-agora__corpo">
          <span className="ev-vivo">{vivo === 'luta' ? t('evento.quadro.aoVivo') : t('evento.quadro.inscricoes')}</span>
          <b>{t('evento.nome')}</b>
          <small>
            {vivo === 'luta'
              ? t('evento.quadro.dePe', { n: atual.vivos, t: formatar(restanteDe(atual.fimEmMs)) })
              : atual.inscrito
                ? t('evento.quadro.inscrito', { n: atual.inscritos })
                : t('evento.quadro.inscritosMin', { n: atual.inscritos, min: minimo })}
          </small>
        </span>
        {vivo === 'luta'
          ? <Pips vivos={atual.vivos} de={porSala} />
          : <span className="ev-relogio">{formatar(restanteDe(atual.fechaEmMs))}</span>}
      </div>
      {vivo === 'inscricao' && (atual.inscrito ? (
        <div className="ev-agora__l">
          <small>{t('evento.quadro.arenaAbre')}</small>
          <button type="button" className="btn btn--mini" disabled={busy} onClick={onDesistir}>{t('evento.desistir')}</button>
        </div>
      ) : (
        <>
          <button type="button" className="btn btn--primary ev-cheio" disabled={busy || !!razao} onClick={onInscrever}>
            {t('evento.inscreverMoeda', { n: numero(atual.taxa), moeda })}
          </button>
          {razao && <p className="ev-dica ev-dica--aviso">{razao}</p>}
        </>
      ))}
    </section>
  );
}

/** O Balron de pé: a vida do shard e o relógio, no topo do quadro. */
function DestaqueChefe({ chefe, onAbrir }: { chefe: ChefeEstado | null; onAbrir(): void }) {
  if (!chefe || !chefeVivo(chefe)) return null;
  const p = Math.max(0, Math.min(100, chefe.permil / 10));
  return (
    <section className="ev-agora" aria-live="polite">
      <div className="ev-agora__t">
        <button type="button" className="ev-agora__abrir" onClick={onAbrir} aria-label={t('chefe.nome')}>
          <Selo id="chefe" />
        </button>
        <span className="ev-agora__corpo">
          <span className="ev-vivo">{t('evento.quadro.aoVivo')}</span>
          <b>{chefe.nome}</b>
          <small>{t('chefe.det.onde', { mapa: nomeDoMapa(chefe.mapa), n: numero(chefe.lutadores) })}</small>
        </span>
        <span className="ev-relogio">{formatar(restanteDe(chefe.fimEmMs))}</span>
      </div>
      <span className="chefe-vida"><i style={{ width: `${p}%` }} /><b>{p.toLocaleString(undefined, { maximumFractionDigits: 1 })}%</b></span>
    </section>
  );
}

function Cartao({ tipo, atual, agenda, chefe, penta, onAbrir }: {
  tipo: TipoEvento; atual: EventoAtual | null; agenda: EventoAgenda | null; chefe: ChefeEstado | null; penta: PentaEstado | null; onAbrir(): void;
}) {
  const s = situacaoDe(tipo.id, atual, agenda, chefe, penta);
  const ub = tipo.id === TIPO_UB;
  const ehChefe = tipo.id === TIPO_CHEFE;
  const porSala = atual?.porSala ?? agenda?.porSala ?? 6;
  const partida = Math.round((agenda?.partidaSegundos || atual?.partidaSegundos || 900) / 60);
  const taxa = agenda?.inscricao ?? atual?.taxa ?? 0;
  const p = proximoDo(tipo.id, agenda);
  return (
    <li>
      <button type="button" className="ev-cartao" onClick={onAbrir} title={p ? new Date(p.emMs).toLocaleString() : undefined}>
        <Selo id={tipo.selo} />
        <span className="ev-cartao__corpo">
          <b className="ev-cartao__nome">{t(tipo.nome)}</b>
          {ub && <span className="ev-cartao__frase">{t('evento.ub.frase', { n: porSala })}</span>}
          {ehChefe && <span className="ev-cartao__frase">{t('chefe.frase')}</span>}
          {tipo.id === TIPO_PENTA && <span className="ev-cartao__frase">{t('penta.frase')}</span>}
          <span className="ev-cartao__tags">
            <span className={`ev-chip ev-chip--${s.tom}`}>{s.texto}</span>
            {ub && (
              <>
                <span className="ev-tag"><svg viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="3.6" r="2.2" /><path d="M1.6 11c.4-2.6 2.2-4 4.4-4s4 1.4 4.4 4z" /></svg>{t('evento.tag.porSala', { n: porSala })}</span>
                <span className="ev-tag"><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M6 1.4a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2zm0 1.5V6l1.8 1.2" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>{t('evento.tag.min', { n: partida })}</span>
                <span className="ev-tag"><svg viewBox="0 0 12 12" aria-hidden="true"><ellipse cx="6" cy="7.6" rx="4.4" ry="2.2" opacity=".55" /><ellipse cx="6" cy="5" rx="4.4" ry="2.2" /></svg>{numero(taxa)}</span>
              </>
            )}
          </span>
        </span>
        <span className="ev-cartao__dir">
          <span className="ev-cartao__seta" aria-hidden="true">›</span>
        </span>
      </button>
    </li>
  );
}
